import { supabase } from '@/lib/supabaseClient';
import { ThemeConfig, ThemeColors, DEFAULT_THEME, PRESET_THEMES } from '@/types/theme';
import { fixThemeForegrounds } from '@/utils/themeFixer';
import { getContrastingTextColor, generateHoverColor } from '@/utils/themeUtils';
import { applySEOSettings } from '@/utils/seo';

// Function to get current tenant ID dynamically
const getCurrentTenantId = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase.rpc('get_current_user_tenant_id');
    if (error) {
      return null;
    }
    return data;
  } catch (error) {
    return null;
  }
};

export interface TenantThemeSettings {
  current_theme: string;
  custom_logo?: string;
  custom_favicon?: string;
  custom_themes?: ThemeConfig[];
  sidebar_color_override?: string;
  custom_seo?: any;
}

/**
 * SUB-1.39 · F-76. The columns these loaders actually use, named.
 *
 * ⚠️ **THIS IS THE INTERFACE ABOVE, AS A SELECT LIST.** Both loaders used
 * `select('*')` and asked for all sixteen columns of `tenant_theme_settings`
 * while their own return type declared six. The type was right; the query was
 * never brought into line with it.
 *
 * ⚠️ **AND IT IS NOW LOAD-BEARING, NOT TIDINESS.** `20260817k` revokes `anon`'s
 * table-wide SELECT and grants back exactly these seven columns, because
 * `loadTenantThemeSettingsByTenantId` runs UNAUTHENTICATED on the public
 * `/customer/:projectId` route. Against that grant, `select('*')` is a
 * `42501 permission denied for column`. Adding a field here without adding it to
 * the grant will fail for anon callers and work fine for everybody else, which
 * is the worst way for it to fail.
 *
 * `tenant_id` is fetched as well as filtered on: a WHERE clause needs SELECT
 * privilege on the column it names.
 */
const THEME_SETTINGS_COLUMNS =
  'tenant_id, current_theme, custom_logo, custom_favicon, custom_themes, sidebar_color_override, custom_seo';

// Function to load tenant theme settings from database
export const loadTenantThemeSettings = async (): Promise<TenantThemeSettings | null> => {
  try {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) {
      return null;
    }

    const { data, error } = await supabase
      .from('tenant_theme_settings')
      .select(THEME_SETTINGS_COLUMNS)
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

// Function to load tenant theme settings by tenant ID (for customer portal)
export const loadTenantThemeSettingsByTenantId = async (tenantId: string): Promise<TenantThemeSettings | null> => {
  try {
    if (!tenantId) {
      return null;
    }

    const { data, error } = await supabase
      .from('tenant_theme_settings')
      .select(THEME_SETTINGS_COLUMNS)
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      return null;
    }

    return data;
  } catch (error) {
    return null;
  }
};

/**
 * Resolve the active ThemeConfig (preset or custom) from tenant theme settings.
 * Pure — no DOM side-effects — so it's reusable outside the page-theming flow (e.g. proposals branding).
 * Falls back to DEFAULT_THEME when settings/current_theme don't resolve.
 */
export const resolveTenantThemeConfig = (settings: TenantThemeSettings | null): ThemeConfig => {
  if (!settings?.current_theme) return DEFAULT_THEME;
  const preset = PRESET_THEMES.find((t) => t.id === settings.current_theme);
  if (preset) return preset;
  const custom = settings.custom_themes?.find((t) => t.id === settings.current_theme);
  return custom ?? DEFAULT_THEME;
};

/**
 * Extract the proposal-relevant brand bits (accent color + logo) from tenant theme settings.
 * Returns nulls when there are no real settings so callers can fall back to their own default theme
 * rather than the app-wide emerald DEFAULT_THEME. Used by the proposals builder to auto-brand.
 */
export const resolveTenantProposalBranding = (
  settings: TenantThemeSettings | null,
): { primary: string | null; logoUrl: string | null } => {
  if (!settings) return { primary: null, logoUrl: null };
  const theme = resolveTenantThemeConfig(settings);
  return {
    primary: theme.colors?.primary ?? null,
    logoUrl: settings.custom_logo ?? theme.logo ?? null,
  };
};

// Function to apply tenant theme to the page
export const applyTenantTheme = (settings: TenantThemeSettings | null, tenantName?: string) => {
  if (!settings) {
    // Apply default theme if no settings found
    applyThemeToPage(DEFAULT_THEME, tenantName);
    return;
  }

  // Find the current theme (preset or custom)
  let theme: ThemeConfig = DEFAULT_THEME;
  
  if (settings.current_theme) {
    // First check preset themes
    const presetTheme = PRESET_THEMES.find(t => t.id === settings.current_theme);
    if (presetTheme) {
      theme = presetTheme;
    } else if (settings.custom_themes) {
      // Then check custom themes
      const customTheme = settings.custom_themes.find(t => t.id === settings.current_theme);
      if (customTheme) {
        theme = customTheme;
      }
    }
  }

  // Apply custom logo and favicon if they exist
  if (settings.custom_logo) {
    theme = {
      ...theme,
      logo: settings.custom_logo,
    };
  }
  
  if (settings.custom_favicon) {
    theme = {
      ...theme,
      favicon: settings.custom_favicon,
    };
  }

  // Apply sidebar color override if it exists and the theme is not custom
  if (settings.sidebar_color_override && !theme.isCustom) {
    theme = {
      ...theme,
      colors: {
        ...theme.colors,
        sidebarColor: settings.sidebar_color_override,
        sidebarTextColor: getContrastingTextColor(settings.sidebar_color_override),
        sidebarHoverBgColor: generateHoverColor(settings.sidebar_color_override, 0.15),
        sidebarHoverTextColor: getContrastingTextColor(generateHoverColor(settings.sidebar_color_override, 0.15)),
      },
    };
  }

  // Apply the theme to the page
  applyThemeToPage(theme, tenantName);

  // Custom SEO is now handled by ThemeContext to prevent flickering
};

/**
 * Fill in every colour a theme does not define, from DEFAULT_THEME.
 *
 * Two real bugs this closes, both of which look like "the tenant theme did not apply".
 *
 * 1. A PARTIAL THEME SILENTLY INHERITS ANOTHER BRAND. `applyThemeToPage` only ever set the keys a
 *    theme actually carried, and the keys it did not set fell through to whatever `index.css`
 *    declares on `:root`. Those defaults were the Solerro gold palette, so a tenant theme that
 *    omitted, say, `popover` and `accentHover` rendered Solerro's brand inside its own app. This
 *    is not hypothetical: of the eight tenants with an active theme, Solerro's `joules-gold`
 *    carries 12 of the 27 colours, so 15 variables came from somewhere else entirely.
 *
 * 2. COLOURS LEAKED ACROSS A SUPERADMIN TENANT SWITCH. `setProperty` only ever adds, and nothing
 *    cleared the previous tenant's variables. Switching from a 27-colour tenant to a 12-colour one
 *    left 15 stale variables from the tenant you just left. Completing the set fixes this for
 *    free: every variable is written on every apply, so there is nothing left to go stale.
 *
 * Exported and pure so the behaviour is testable without a DOM.
 */
export const completeThemeColors = (colors: Partial<ThemeColors> | undefined): ThemeColors => {
  const defined = Object.fromEntries(
    // A key that is present but empty/undefined must NOT beat the default, or the spread below
    // reintroduces exactly the hole this function exists to fill.
    Object.entries(colors ?? {}).filter(([, v]) => typeof v === 'string' && v.trim() !== '')
  );
  return { ...DEFAULT_THEME.colors, ...defined } as ThemeColors;
};

// Function to apply theme to the page
const applyThemeToPage = (theme: ThemeConfig, tenantName?: string) => {
  // Complete the palette BEFORE fixing foregrounds, so the foreground fixer derives its contrast
  // from the colour that will actually be rendered rather than from a missing one.
  const completeTheme: ThemeConfig = { ...theme, colors: completeThemeColors(theme.colors) };
  // Fix theme foregrounds before applying
  const fixedTheme = fixThemeForegrounds(completeTheme);
  const root = document.documentElement;

  // Apply color variables
  Object.entries(fixedTheme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });

  // Apply logo and favicon
  if (fixedTheme.logo) {
    root.style.setProperty('--logo-url', `url(${fixedTheme.logo})`);
  }
  if (fixedTheme.favicon) {
    root.style.setProperty('--favicon-url', `url(${fixedTheme.favicon})`);
  }

  // Apply sidebar colors
  if (fixedTheme.colors.sidebarColor) {
    root.style.setProperty('--sidebar-color', fixedTheme.colors.sidebarColor);
  }
  if (fixedTheme.colors.sidebarTextColor) {
    root.style.setProperty('--sidebar-text-color', fixedTheme.colors.sidebarTextColor);
  }
  if (fixedTheme.colors.sidebarHoverBgColor) {
    root.style.setProperty('--sidebar-hover-bg-color', fixedTheme.colors.sidebarHoverBgColor);
  }
  if (fixedTheme.colors.sidebarHoverTextColor) {
    root.style.setProperty('--sidebar-hover-text-color', fixedTheme.colors.sidebarHoverTextColor);
  }

  // Update favicon in head
  const faviconLink = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
  if (faviconLink && fixedTheme.favicon) {
    faviconLink.href = fixedTheme.favicon;
  }

  // SEO is now handled by ThemeContext to prevent flickering
};

// Function to get the current logo URL from theme settings
export const getTenantLogo = async (): Promise<string | null> => {
  const settings = await loadTenantThemeSettings();
  if (settings?.custom_logo) {
    return settings.custom_logo;
  }
  return null;
};

// Function to get the current favicon URL from theme settings
export const getTenantFavicon = async (): Promise<string | null> => {
  const settings = await loadTenantThemeSettings();
  if (settings?.custom_favicon) {
    return settings.custom_favicon;
  }
  return null;
}; 